import { ASSETS } from './assets-data'

const shippingData = {
  en: {
    seo: {
      title: 'Shipping & Returns | BH Hardware',
      description: 'BH Hardware shipping policies, return eligibility, restocking fees, and instructions for domestic and international orders.',
      keywords: 'BH Hardware shipping, return policy, garage door parts shipping, hardware store returns, bhhardware.net'
    },
    heroSection: {
      image: "/placeholder.svg",
      overlay: "bg-black/60"
    },
    title: 'Shipping & Returns',
    effectiveDateLabel: 'Last Updated',
    effectiveDate: 'October 1, 2025',

    introParagraphs: [
      'We are committed to getting your order to you quickly and handling returns fairly. Please read the policies below carefully before placing your order.'
    ],

    sections: [
      {
        id: 'domestic-shipping',
        number: 1,
        heading: 'Domestic Shipping (U.S.)',
        paragraphs: [
          'All orders are processed within 1–3 business days. Orders placed before 2 PM EST may ship the same day.',
          'Shipping rates and estimated taxes are calculated at checkout in USD. Standard shipping is via UPS, unless otherwise requested.',
          'Shipping times are estimates only and may be subject to carrier delays. BH Hardware is not liable for delays caused by carriers.'
        ]
      },
      {
        id: 'international-shipping',
        number: 2,
        heading: 'International Shipping',
        paragraphs: [
          'Orders shipped outside the U.S. may require an additional 3–5 business days for processing.',
          'Customers are responsible for any customs duties, taxes, or import fees charged by their country.',
          'All international shipments are sent via trackable courier. BH Hardware is not responsible for delays or seizures caused by customs.'
        ]
      },
      {
        id: 'address-changes',
        number: 3,
        heading: 'Address Changes',
        paragraphs: [
          'Shipping addresses can only be changed within 15 minutes of payment confirmation. Please verify all information before submitting your order.'
        ]
      },
      {
        id: 'return-eligibility',
        number: 4,
        heading: 'Return Eligibility',
        paragraphs: [
          'Returns must be requested within 30 days of confirmed delivery.',
          'All returned items must be unused, in original packaging, and in resellable condition, including all accessories, manuals, and labels.',
          'A Return Merchandise Authorization (RMA) is required before sending back any item. To obtain an RMA, contact us at admin@bh-hardware.net.'
        ]
      },
      {
        id: 'non-returnable',
        number: 5,
        heading: 'Non-Returnable Items',
        paragraphs: [
          'The following items are final sale and non-refundable:',
          'Custom torsion springs.',
          'Items marked as clearance, special order, or custom-built.',
          'Any items that have been used, modified, or installed.'
        ]
      },
      {
        id: 'restocking',
        number: 6,
        heading: 'Restocking Fee',
        paragraphs: [
          'Approved returns are subject to a 20% restocking fee. Original shipping charges are non-refundable.',
          'Any return sent without an RMA or in poor condition may be denied or charged additional restocking fees at our discretion.'
        ]
      },
      {
        id: 'return-shipping',
        number: 7,
        heading: 'Return Shipping',
        paragraphs: [
          'Customers are responsible for the cost and risk of return shipping. BH Hardware is not responsible for lost, damaged, or untrackable return shipments.',
          'We recommend using a trackable and insured shipping method for all returns.'
        ]
      },
      {
        id: 'defective',
        number: 8,
        heading: 'Defective or Damaged Items',
        paragraphs: [
          'If your order arrives damaged or with a manufacturer defect, notify us within 5 business days of delivery at admin@bh-hardware.net.',
          'Provide photos of the packaging and damaged item(s) for verification. Upon approval, we will provide a replacement or refund at no cost, including shipping.',
          'Items returned as defective but found functional may incur a standard restocking fee.'
        ]
      }
    ],

    contactTitle: 'Need Help with a Return?',
    contactIntro: (info) => `Email us at ${info.email || 'admin@bh-hardware.net'} or call ${info.phone || ''} for assistance with returns or shipping questions.`,

    conversionSection: {
      title: 'Questions About Your Order?',
      subtitle: 'Our team is here to help. Reach out and we\'ll get back to you as quickly as possible.',
      bgImage: "https://sawpy-assets.s3.us-east-2.amazonaws.com/BH-Hardware/backgrounds/warehouse_bg.jpg",
      bgColor: "bg-gray-50 dark:bg-zinc-900",
      buttonText: 'Return Home'
    }
  },

  he: {
    seo: {
      title: 'משלוח והחזרות | BH Hardware',
      description: 'מדיניות משלוח והחזרות של BH Hardware, כולל זכאות להחזרה, דמי טיפול והוראות.',
      keywords: 'משלוח BH Hardware, מדיניות החזרה, bhhardware.net'
    },
    heroSection: {
      image: "https://sawpy-assets.s3.us-east-2.amazonaws.com/BH-Hardware/backgrounds/warehouse_bg.jpg",
      overlay: "bg-black/60"
    },
    title: 'משלוח והחזרות',
    effectiveDateLabel: 'עודכן לאחרונה',
    effectiveDate: '1 באוקטובר 2025',

    introParagraphs: [
      'אנו מחויבים לספק את הזמנתכם במהירות ולטפל בהחזרות באופן הוגן. אנא קראו את המדיניות הבאה לפני ביצוע הזמנה.'
    ],

    sections: [
      {
        id: 'domestic-shipping',
        number: 1,
        heading: 'משלוח מקומי (ארה"ב)',
        paragraphs: [
          'כל ההזמנות מעובדות תוך 1–3 ימי עסקים. הזמנות שבוצעו לפני 14:00 EST עשויות להישלח באותו יום.',
          'עלויות משלוח ומיסים מחושבים בקופה. משלוח סטנדרטי הוא דרך UPS.',
          'זמני משלוח הם הערכות בלבד. BH Hardware אינה אחראית לעיכובים הנגרמים על ידי חברת השילוח.'
        ]
      },
      {
        id: 'international-shipping',
        number: 2,
        heading: 'משלוח בינלאומי',
        paragraphs: [
          'הזמנות מחוץ לארה"ב עשויות לדרוש 3–5 ימי עסקים נוספים לעיבוד.',
          'הלקוח אחראי לכל מכסים, מיסים או עמלות ייבוא שגובה מדינתו.',
          'BH Hardware אינה אחראית לעיכובים הנגרמים ממכס.'
        ]
      },
      {
        id: 'return-eligibility',
        number: 3,
        heading: 'זכאות להחזרה',
        paragraphs: [
          'ניתן לבקש החזרה תוך 30 יום מאישור האספקה.',
          'כל הפריטים המוחזרים חייבים להיות ללא שימוש, באריזה המקורית ובמצב מכירה.',
          'נדרש אישור החזרה (RMA) לפני משלוח. לקבלת RMA, צרו קשר בכתובת admin@bh-hardware.net.'
        ]
      },
      {
        id: 'non-returnable',
        number: 4,
        heading: 'פריטים שאינם ניתנים להחזרה',
        paragraphs: [
          'הפריטים הבאים הם מכירה סופית ואינם ניתנים להחזר כספי:',
          'קפיצי תלייה מותאמים אישית.',
          'פריטי סוף מלאי, הזמנות מיוחדות או מוצרים מותאמים.',
          'כל פריט שהיה בשימוש, שונה או הותקן.'
        ]
      },
      {
        id: 'restocking',
        number: 5,
        heading: 'דמי טיפול',
        paragraphs: [
          'החזרות מאושרות כפופות לדמי טיפול של 20%. עלויות משלוח מקוריות אינן ניתנות להחזר.'
        ]
      },
      {
        id: 'defective',
        number: 6,
        heading: 'פריטים פגומים או פגועים',
        paragraphs: [
          'אם הזמנתכם הגיעה פגומה, הודיעו לנו תוך 5 ימי עסקים מהאספקה בכתובת admin@bh-hardware.net.',
          'עם אישור, נספק החלפה או החזר כספי ללא עלות, כולל משלוח.'
        ]
      }
    ],

    contactTitle: 'זקוקים לעזרה עם החזרה?',
    contactIntro: (info) => `שלחו אימייל ל-${info.email || 'admin@bh-hardware.net'} או התקשרו ל-${info.phone || '+1 855-786-7504'} לסיוע.`,

    conversionSection: {
      title: 'שאלות על ההזמנה שלכם?',
      subtitle: 'הצוות שלנו כאן לעזור. צרו קשר ונחזור אליכם בהקדם האפשרי.',
      bgImage: "https://sawpy-assets.s3.us-east-2.amazonaws.com/BH-Hardware/backgrounds/warehouse_bg.jpg",
      bgColor: "bg-gray-50 dark:bg-zinc-900",
      buttonText: 'חזרה לדף הבית'
    }
  }
};

export function getShippingStrings(language) {
  return shippingData[language] || shippingData.en;
}

export default shippingData;
